import { ErrorMessage } from "@/components/ErrorMessage";
import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { InputField } from "@/components/podkit/forms/InputField";
import { Textarea } from "@/components/podkit/forms/Textarea";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useUpdateServiceAccountSecretValue } from "@/queries/service-account-secret-queries";
import type { PlainSecret } from "@/queries/secret-queries";
import { formatError } from "@/utils/errors";
import { useCallback, useId, useState, type FC } from "react";
import type { ServiceAccountHeaders } from "@/api";

type Props = {
    secret: PlainSecret;
    serviceAccountId: string;
    headers: ServiceAccountHeaders;
    onClose: () => void;
};

export const ServiceAccountEditSecretModal: FC<Props> = ({ secret, serviceAccountId, headers, onClose }) => {
    const { toast } = useToast();
    const updateSecretValue = useUpdateServiceAccountSecretValue({ headers });

    const secretId = useId();
    const [secretValue, setSecretValue] = useState<string>("");

    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (!nextOpen) {
                onClose();
            }
        },
        [onClose],
    );

    const handleSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            try {
                await updateSecretValue.mutateAsync({
                    serviceAccountId,
                    secretId: secret.id,
                    plaintextValue: secretValue,
                });
                toast({
                    title: `${secret.name} was successfully updated`,
                });
                onClose();
            } catch (e) {
                toast({
                    title: "Failed to update secret",
                    description: formatError(e),
                });
            }
        },
        [serviceAccountId, secret.id, secret.name, secretValue, updateSecretValue, onClose, toast],
    );

    const isValid = secretValue.length > 0;

    return (
        <Dialog open onOpenChange={handleOpenChange}>
            <DialogContent
                className="max-w-[600px]"
                data-testid="service-account-edit-secret-modal-content"
                data-track-location={TrackLocations.EditSecretModal}
            >
                <DialogHeader>
                    <DialogTitle>Edit secret</DialogTitle>
                    <DialogDescription>
                        Update the secret value for <span className="font-mono font-bold">{secret.name}</span>.
                    </DialogDescription>
                </DialogHeader>

                <DialogBody className="overflow-x max-w-full">
                    <ErrorMessage
                        error={updateSecretValue.error}
                        data-testid="error-message-service-account-secret-update-failed"
                        className="text-center"
                    />
                    <form
                        onSubmit={handleSubmit}
                        id="service-account-edit-secret-form"
                        data-testid="service-account-edit-secret-form"
                        className="flex flex-col gap-4"
                    >
                        <InputField id={secretId} label="New secret value">
                            <Textarea
                                id={secretId}
                                name="secret-value"
                                rows={5}
                                dontAutosize={true}
                                value={secretValue}
                                placeholder="Enter new secret value"
                                onChange={(e) => setSecretValue(e.target.value)}
                                className="resize-none"
                                data-testid="service-account-secret-value-input"
                            />
                        </InputField>
                    </form>
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button type="button" variant="outline">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        variant="primary"
                        form="service-account-edit-secret-form"
                        loading={updateSecretValue.isPending}
                        disabled={updateSecretValue.isPending || !isValid}
                        data-testid="service-account-edit-secret-submit-button"
                        data-tracking-id="confirm-update-secret-service-account-edit-secret-modal"
                    >
                        Update
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
